'use client';

import ReportModalTimelineChart from '@/components/products-report-modal/ReportModalTimelineChart';
import {
    ReportModalAverageRatingChartData,
    ReportModalRatingDistributionChartData,
    ReportModalResponseTimeChartData,
    buildReportModalTreemapTileViewModel,
    formatReportModalMinutesValue,
} from '@/domain/reporting/reporting-modal-charts';
import {
    formatInteger,
    formatPercent,
    formatRating,
    formatRatingWithStar,
} from '@/domain/reporting/reporting-shared.formatters';
import { TreemapLayoutItem } from '@/domain/reporting/reporting-shared.types';
import { AibackReportPdfTopItemEntity } from '@/entities/aiback-report.entity';
import Rating from '@/ui/Rating';
import { FC, ReactNode } from 'react';

type ReportModalTopAndTreemapSectionProps = {
    totalReviews: number;
    totalAnswers: number;
    answersShare: number | null;
    companyAverageRating: number | null;
    averageResponseTimeMinutes: number | null;
    answeredReviewsCount: number;
    ratingDistributionChartData: ReportModalRatingDistributionChartData | null;
    averageRatingChartData: ReportModalAverageRatingChartData | null;
    responseTimeChartData: ReportModalResponseTimeChartData | null;
    channelsCount: number;
    productsCount: number;
    brandsCount: number;
    topChannels: AibackReportPdfTopItemEntity[];
    topProducts: AibackReportPdfTopItemEntity[];
    topBrands: AibackReportPdfTopItemEntity[];
    onOpenProducts: () => void;
    onOpenBrands: () => void;
    onCloseModal: () => void;
    treemapLayout: TreemapLayoutItem[];
};

type TopTableCardProps = {
    title: string;
    rows: AibackReportPdfTopItemEntity[];
    emptyLabel: string;
    actionLabel?: string;
    onAction?: () => void;
};

const TopTableCard: FC<TopTableCardProps> = ({ title, rows, emptyLabel, actionLabel, onAction }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
            {actionLabel && onAction ? (
                <button
                    type="button"
                    aria-label={`${actionLabel} ${title}`}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    onClick={onAction}
                >
                    {actionLabel}
                </button>
            ) : null}
        </div>
        <table className="w-full table-fixed text-xs text-slate-700">
            <tbody>
                {rows.map((row, index) => (
                    <tr key={`${title}-${index}-${row.name}`} className="border-b border-slate-100 last:border-b-0">
                        <td className="w-6 px-1 py-1.5 text-center">{index + 1}</td>
                        <td className="truncate px-2 py-1.5" title={row.name}>
                            {row.name}
                        </td>
                        <td className="w-[64px] px-1 py-1.5 text-right">{formatInteger(row.reviewCount)}</td>
                        <td className="w-[68px] px-1 py-1.5 text-right whitespace-nowrap">
                            {formatRatingWithStar(row.averageRating)}
                        </td>
                    </tr>
                ))}
                {!rows.length ? (
                    <tr>
                        <td className="px-2 py-3 text-center text-slate-500" colSpan={4}>
                            {emptyLabel}
                        </td>
                    </tr>
                ) : null}
            </tbody>
        </table>
    </div>
);

type ChartCardProps = {
    title: string;
    value: ReactNode;
    subtitle?: ReactNode;
    children: ReactNode;
};

const ChartCard: FC<ChartCardProps> = ({ title, value, subtitle, children }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="mb-3 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{title}</div>
            <div className="text-base font-semibold text-slate-900">{value}</div>
            {subtitle ? <div className="text-xs text-slate-500">{subtitle}</div> : null}
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2">{children}</div>
    </div>
);

const ReportModalTopAndTreemapSection: FC<ReportModalTopAndTreemapSectionProps> = ({
    totalReviews,
    totalAnswers,
    answersShare,
    companyAverageRating,
    averageResponseTimeMinutes,
    answeredReviewsCount,
    ratingDistributionChartData,
    averageRatingChartData,
    responseTimeChartData,
    channelsCount,
    productsCount,
    brandsCount,
    topChannels,
    topProducts,
    topBrands,
    onOpenProducts,
    onOpenBrands,
    onCloseModal,
    treemapLayout,
}) => {
    const hasAverageResponseTime = averageResponseTimeMinutes !== null && Number.isFinite(averageResponseTimeMinutes);

    return (
        <>
            <div className="space-y-4 xl:col-span-8">
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <ChartCard
                        title="Отзывы с оценками"
                        value={formatInteger(totalReviews)}
                        subtitle={
                            <span>
                                Отвечено {formatInteger(totalAnswers)} ({formatPercent(answersShare, 1)})
                            </span>
                        }
                    >
                        {ratingDistributionChartData ? (
                            <svg
                                className="block w-full"
                                width="100%"
                                height={ratingDistributionChartData.height}
                                viewBox={`0 0 ${ratingDistributionChartData.width} ${ratingDistributionChartData.height}`}
                                role="img"
                                aria-label="График распределения отзывов по оценкам"
                            >
                                {ratingDistributionChartData.rows.map((row) => (
                                    <g key={`rating-distribution-${row.stars}`}>
                                        <text
                                            x={ratingDistributionChartData.leftLabelWidth - 4}
                                            y={row.y + ratingDistributionChartData.rowHeight - 3}
                                            fontSize="10"
                                            fill="#475569"
                                            textAnchor="end"
                                        >
                                            {'★'.repeat(Math.max(1, row.stars))}
                                        </text>
                                        <rect
                                            x={ratingDistributionChartData.leftLabelWidth}
                                            y={row.y}
                                            width={ratingDistributionChartData.chartAreaWidth}
                                            height={ratingDistributionChartData.rowHeight}
                                            rx="3"
                                            ry="3"
                                            fill="#e2e8f0"
                                        />
                                        <rect
                                            x={ratingDistributionChartData.leftLabelWidth}
                                            y={row.y}
                                            width={row.barWidth}
                                            height={ratingDistributionChartData.rowHeight}
                                            rx="3"
                                            ry="3"
                                            fill={row.color}
                                        />
                                        <text
                                            x={
                                                ratingDistributionChartData.leftLabelWidth +
                                                ratingDistributionChartData.chartAreaWidth +
                                                6
                                            }
                                            y={row.y + ratingDistributionChartData.rowHeight - 3}
                                            fontSize="10"
                                            fill="#334155"
                                            textAnchor="start"
                                        >
                                            {formatInteger(row.value)}
                                        </text>
                                    </g>
                                ))}
                            </svg>
                        ) : (
                            <div className="flex h-[116px] items-center justify-center text-xs text-slate-500">
                                Нет данных по распределению отзывов.
                            </div>
                        )}
                    </ChartCard>

                    <ChartCard
                        title="Ср. оценка"
                        value={
                            <span className="flex flex-wrap items-center gap-2">
                                {companyAverageRating !== null ? (
                                    <Rating value={companyAverageRating} size={12} />
                                ) : null}
                                <span>{formatRating(companyAverageRating)}</span>
                            </span>
                        }
                        subtitle={<span aria-hidden="true">{'\u00A0'}</span>}
                    >
                        <ReportModalTimelineChart
                            chartData={averageRatingChartData}
                            ariaLabel="Динамика средней оценки"
                            emptyLabel="Нет данных по динамике средней оценки."
                        />
                    </ChartCard>

                    <ChartCard
                        title="Ср. время ответа"
                        value={
                            <span>
                                {formatReportModalMinutesValue(averageResponseTimeMinutes)}
                                {hasAverageResponseTime ? ' минут' : ''}
                            </span>
                        }
                        subtitle={<span>По {formatInteger(answeredReviewsCount)} ответам</span>}
                    >
                        <ReportModalTimelineChart
                            chartData={responseTimeChartData}
                            ariaLabel="Динамика среднего времени ответа"
                            emptyLabel="Нет данных по динамике времени ответа."
                            lineColor="#0f766e"
                            tickFormatter={(value) => formatReportModalMinutesValue(value)}
                        />
                    </ChartCard>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <TopTableCard
                        title={`Σ каналов: ${formatInteger(channelsCount)}`}
                        rows={topChannels}
                        emptyLabel="Нет данных по каналам."
                    />
                    <TopTableCard
                        title={`Σ брендов: ${formatInteger(brandsCount)}`}
                        rows={topBrands}
                        emptyLabel="Нет данных по брендам."
                        actionLabel="См. все"
                        onAction={() => {
                            onOpenBrands();
                            onCloseModal();
                        }}
                    />
                    <TopTableCard
                        title={`Σ продуктов: ${formatInteger(productsCount)}`}
                        rows={topProducts}
                        emptyLabel="Нет данных по продуктам."
                        actionLabel="См. все"
                        onAction={() => {
                            onOpenProducts();
                            onCloseModal();
                        }}
                    />
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 xl:col-span-12">
                <div className="mb-3 flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-900">Карта продуктов по отзывам и оценке</h4>
                    <span className="text-xs text-slate-500">Размер = отзывы, цвет = средняя оценка</span>
                </div>
                <div className="h-[320px] w-full rounded-md border border-slate-200 bg-white p-2 xl:h-[360px]">
                    {treemapLayout.length ? (
                        <div className="relative h-full w-full overflow-hidden rounded-sm">
                            {treemapLayout.map((item) => {
                                const tile = buildReportModalTreemapTileViewModel(item);

                                return (
                                    <div
                                        key={tile.id}
                                        className="absolute overflow-hidden border border-white/70"
                                        style={{
                                            left: tile.left,
                                            top: tile.top,
                                            width: tile.width,
                                            height: tile.height,
                                            backgroundColor: tile.backgroundColor,
                                            color: tile.textColor,
                                        }}
                                        title={tile.title}
                                    >
                                        <div className={tile.contentClassName}>
                                            {tile.label ? (
                                                <div className={tile.labelClassName}>{tile.label}</div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex h-full items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
                            Нет данных для диаграммы покрытия.
                        </div>
                    )}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                    Площадь блока = количество отзывов. Цвет = оценка (4-5: зеленый, 3-4: желтый, 1-3: красный).
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
                    <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5">
                        <span className="h-2.5 w-2.5 rounded bg-emerald-500" />
                        4-5
                    </span>
                    <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5">
                        <span className="h-2.5 w-2.5 rounded bg-amber-500" />
                        3-4
                    </span>
                    <span className="inline-flex items-center gap-1 rounded border border-rose-200 bg-rose-50 px-2 py-0.5">
                        <span className="h-2.5 w-2.5 rounded bg-rose-500" />
                        1-3
                    </span>
                </div>
            </div>
        </>
    );
};

export default ReportModalTopAndTreemapSection;
